import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { COMPANY, SERVICES, INDUSTRIES } from "@/data/content";

const Footer = () => {
  return (
    <footer className="bg-[#0F172A] text-slate-300 relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
      <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8 pt-20 pb-10">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          {/* Overview */}
          <div>
            <Logo light />
            <p className="mt-6 text-sm leading-relaxed text-slate-400 max-w-xs">
              Specialized automotive certification and regulatory consulting — helping manufacturers
              achieve type approval, homologation and compliance across India, faster.
            </p>
            <a
              href={COMPANY.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin"
              className="mt-6 inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-700 hover:border-[#2563EB] hover:bg-[#1E40AF] transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-white font-display font-semibold text-sm tracking-wide mb-5">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              {[
                { label: "Home", to: "/" },
                { label: "About Us", to: "/about" },
                { label: "Our Team", to: "/team" },
                { label: "Industries", to: "/industries" },
                { label: "Book a Consultation", to: "/book-consultation" },
              ].map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-slate-400 hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-display font-semibold text-sm tracking-wide mb-5">Services</h4>
            <ul className="space-y-3 text-sm">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services#${s.slug}`} className="text-slate-400 hover:text-white transition-colors">
                    {s.title.replace(/\s*\(.*\)/, "")}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-display font-semibold text-sm tracking-wide mb-5">Contact</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[#2563EB] shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="text-slate-400 hover:text-white transition-colors break-all">{COMPANY.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[#2563EB] shrink-0" />
                <a href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`} className="text-slate-400 hover:text-white transition-colors">{COMPANY.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#2563EB] shrink-0" />
                <span className="text-slate-400">{COMPANY.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
          <Link to="/book-consultation" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors">
            {COMPANY.domain} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
